import {
  Banknote,
  BarChart3,
  Bot,
  Briefcase,
  Building2,
  CalendarCheck,
  CalendarClock,
  ClipboardCheck,
  Contact2,
  CreditCard,
  FileSignature,
  FileStack,
  FileText,
  FolderKanban,
  GitBranch,
  KanbanSquare,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  Mail,
  Megaphone,
  MessageCircle,
  Receipt,
  Repeat,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  TrendingUp,
  UserCog,
  UserPlus,
  Users,
  Users2,
  Wallet,
} from 'lucide-react';
import { usePermission } from '@/hooks/use-permission';

export type NavItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Hidden unless the user holds this permission. */
  permission?: string;
  /** Hidden unless the user holds at least one of these. */
  anyOf?: string[];
  end?: boolean;
};

export type NavSection = {
  label: string;
  items: NavItem[];
};

/**
 * What the dashboard and My day actually draw on. A role built for one job —
 * somebody who only enters leads — holds none of these, and for them the
 * two overview screens would be empty boxes with a menu pointing at them.
 */
const DASHBOARD_ANY = [
  'admin.user.view',
  'admin.role.view',
  'admin.team.view',
  'report.dashboard.view',
  'crm.deal.view',
  'finance.invoice.view',
  'project.project.view',
  'service.ticket.view',
];
const MY_DAY_ANY = ['crm.task.view', 'crm.deal.view', 'project.task.view', 'service.ticket.view'];

export const SECTIONS: NavSection[] = [
  {
    label: 'Workspace',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true, anyOf: DASHBOARD_ANY },
      { to: '/my-day', label: 'My day', icon: CalendarCheck, anyOf: MY_DAY_ANY },
      { to: '/chat', label: 'Chat', icon: MessageCircle, permission: 'chat.channel.view' },
      {
        to: '/insights',
        label: 'Insights',
        icon: BarChart3,
        permission: 'report.dashboard.view',
      },
      { to: '/reports', label: 'Reports', icon: ClipboardCheck, permission: 'report.report.view' },
    ],
  },
  {
    label: 'Sales',
    items: [
      { to: '/crm/leads', label: 'Leads', icon: UserPlus, permission: 'crm.lead.view', end: true },
      { to: '/crm/leads/board', label: 'Lead pipeline', icon: LayoutGrid, permission: 'crm.lead.view' },
      { to: '/crm/deals', label: 'Deal pipeline', icon: Briefcase, permission: 'crm.deal.view' },
      { to: '/crm/quotes', label: 'Quotes', icon: FileSignature, permission: 'crm.quote.view' },
      { to: '/crm/companies', label: 'Companies', icon: Building2, permission: 'crm.company.view' },
      { to: '/crm/contacts', label: 'Contacts', icon: Contact2, permission: 'crm.contact.view' },
      {
        to: '/crm/pipelines/deal',
        label: 'Deal pipeline setup',
        icon: KanbanSquare,
        permission: 'crm.pipeline.create',
      },
      {
        to: '/crm/pipelines/lead',
        label: 'Lead pipeline setup',
        icon: GitBranch,
        permission: 'crm.pipeline.create',
      },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/finance', label: 'Overview', icon: Wallet, permission: 'finance.invoice.view', end: true },
      { to: '/finance/invoices', label: 'Invoices', icon: Receipt, permission: 'finance.invoice.view' },
      { to: '/finance/payments', label: 'Payments', icon: Banknote, permission: 'finance.payment.view' },
      { to: '/finance/expenses', label: 'Expenses', icon: CreditCard, permission: 'finance.expense.view' },
      {
        to: '/finance/subscriptions',
        label: 'Subscriptions',
        icon: Repeat,
        permission: 'product.subscription.view',
      },
      { to: '/finance/reports', label: 'Reports', icon: BarChart3, permission: 'finance.ledger.view' },
      {
        to: '/finance/forecast',
        label: 'Cash forecast',
        icon: TrendingUp,
        permission: 'finance.forecast.view',
      },
      { to: '/finance/budgets', label: 'Budgets', icon: Target, permission: 'finance.budget.view' },
      {
        to: '/finance/settings',
        label: 'Finance setup',
        icon: SlidersHorizontal,
        permission: 'finance.settings.view',
      },
    ],
  },
  {
    label: 'Delivery',
    items: [
      { to: '/delivery/projects', label: 'Projects', icon: FolderKanban, permission: 'project.project.view' },
      { to: '/delivery/board', label: 'Work board', icon: LayoutGrid, permission: 'project.task.view' },
      {
        to: '/delivery/timesheets',
        label: 'Timesheets',
        icon: ClipboardCheck,
        permission: 'project.timesheet.view',
      },
    ],
  },
  {
    label: 'Service',
    items: [
      { to: '/service/tickets', label: 'Tickets', icon: LifeBuoy, permission: 'service.ticket.view' },
      { to: '/service/contracts', label: 'Contracts', icon: FileSignature, permission: 'service.contract.view' },
      {
        to: '/service/settings',
        label: 'Service setup',
        icon: CalendarClock,
        permission: 'service.sla.view',
      },
    ],
  },
  {
    label: 'Documents',
    items: [
      { to: '/documents', label: 'Vault', icon: FileStack, permission: 'file.document.view', end: true },
      {
        to: '/documents/templates',
        label: 'Templates & signing',
        icon: FileSignature,
        permission: 'file.document.view',
      },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { to: '/marketing/campaigns', label: 'Campaigns', icon: Mail, permission: 'marketing.campaign.view' },
      { to: '/marketing/segments', label: 'Audiences', icon: Users2, permission: 'marketing.segment.view' },
      { to: '/marketing/journeys', label: 'Journeys', icon: GitBranch, permission: 'marketing.campaign.view' },
      {
        to: '/marketing/whatsapp',
        label: 'WhatsApp',
        icon: MessageCircle,
        permission: 'marketing.campaign.view',
      },
      {
        to: '/marketing/settings',
        label: 'Marketing setup',
        icon: Megaphone,
        permission: 'marketing.campaign.view',
      },
    ],
  },
  {
    label: 'Administration',
    items: [
      { to: '/admin/users', label: 'People', icon: Users, permission: 'admin.user.view' },
      { to: '/admin/roles', label: 'Roles', icon: ShieldCheck, permission: 'admin.role.view' },
      { to: '/admin/teams', label: 'Teams', icon: UserCog, permission: 'admin.team.view' },
      {
        to: '/admin/organization',
        label: 'Organisation',
        icon: Settings,
        permission: 'admin.organization.view',
      },
      { to: '/admin/audit', label: 'Audit log', icon: FileText, permission: 'admin.audit.view' },
      {
        to: '/admin/workflows',
        label: 'Automation',
        icon: Bot,
        permission: 'admin.workflow.view',
      },
      {
        to: '/admin/portal-users',
        label: 'Portal access',
        icon: ShieldCheck,
        permission: 'portal.user.view',
      },
    ],
  },
];

/**
 * The order the phone's bottom bar fills its two "quick" slots in: the
 * screens somebody opens many times a day, most specific job first.
 */
const QUICK_PRIORITY = [
  '/crm/leads',
  '/crm/deals',
  '/service/tickets',
  '/delivery/board',
  '/finance/invoices',
  '/my-day',
  '/delivery/projects',
  '/marketing/campaigns',
  '/admin/users',
];

export function isVisible(item: NavItem, can: (key: string) => boolean): boolean {
  if (item.permission && !can(item.permission)) return false;
  if (item.anyOf && !item.anyOf.some(can)) return false;
  return true;
}

/** The sections this user can see, with the items they cannot use removed. */
export function useVisibleSections(): NavSection[] {
  const { can } = usePermission();
  return SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => isVisible(item, can)),
  })).filter((section) => section.items.length > 0);
}

/**
 * Where "home" is for this user: the dashboard when they can use it, otherwise
 * the first screen they can — a lead-entry role lands on Leads, not on a
 * dashboard of things they cannot open.
 */
export function useHomeRoute(): string {
  const sections = useVisibleSections();
  const first = sections[0]?.items[0];
  return first?.to ?? '/account';
}

/** The phone's bottom bar: home, two quick screens, chat, and the full menu. */
export function useQuickItems(): NavItem[] {
  const sections = useVisibleSections();
  const items = sections.flatMap((section) => section.items);
  const byPath = new Map(items.map((item) => [item.to, item]));
  const quick = QUICK_PRIORITY.map((to) => byPath.get(to)).filter((item): item is NavItem => Boolean(item));
  return quick.slice(0, 2);
}

/** Whether the dashboard screen has anything to show this user. */
export function useCanSeeDashboard(): boolean {
  const { canAny } = usePermission();
  return canAny(...DASHBOARD_ANY);
}

export function useCanSeeMyDay(): boolean {
  const { canAny } = usePermission();
  return canAny(...MY_DAY_ANY);
}
